import { uiText, useUiLanguage } from '@/i18n/interface';

import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Trophy } from 'lucide-react';

interface BeltLevel {
  id: string;
  name: string;
  color: string;
  order_sequence: number;
  requirements: any;
}

interface CurrentBeltStatusProps {
  currentBelt: BeltLevel;
  nextBelt: BeltLevel | null;
  attendanceCount: number;
  requiredAttendance: number;
  isEligibleForPromotion: boolean;
  completedRequirements: number;
  totalRequirements: number;
}

const CurrentBeltStatus: React.FC<CurrentBeltStatusProps> = ({
  currentBelt,
  nextBelt,
  attendanceCount,
  requiredAttendance,
  isEligibleForPromotion,
  completedRequirements,
  totalRequirements
}) => {
  useUiLanguage();
  const progressPercentage = totalRequirements > 0 ? (completedRequirements / totalRequirements) * 100 : 0;

  const getBeltColorClass = (color: string) => {
    switch (color) {
      case 'white': return 'border-gray-300 text-gray-800';
      case 'orange': return 'border-orange-500 text-orange-700';
      case 'blue': return 'border-blue-500 text-blue-700';
      case 'yellow': return 'border-yellow-500 text-yellow-700';
      case 'green': return 'border-green-500 text-green-700';
      case 'purple': return 'border-purple-500 text-purple-700';
      case 'brown': return 'border-amber-700 text-amber-800';
      default: return 'border-gray-900 text-gray-900';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Trophy className="h-5 w-5" />
          {uiText("Current Belt Level")}</CardTitle>
        <CardDescription>{uiText("Your current rank and progress")}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Badge
                variant="outline"
                className={`px-3 py-1 text-sm font-medium border-2 ${getBeltColorClass(currentBelt?.color)}`}
              >
                {currentBelt?.name}
              </Badge>
              {isEligibleForPromotion && (
                <Badge className="bg-green-600 text-white">
                  {uiText("Eligible for Promotion")}</Badge>
              )}
            </div>
            <p className="text-sm text-gray-600">
              {uiText("Training Attendance:")}{' '}{attendanceCount}/{requiredAttendance} {' '}{uiText("sessions")}</p>
            <p className="text-sm text-gray-600">
              {uiText("Progress toward next level:")}{' '}{completedRequirements}/{totalRequirements} {' '}{uiText("requirements")}</p>
          </div>
        </div>

        {/* Training Attendance Progress */}
        <div className="mb-4">
          <div className="flex justify-between text-sm mb-2">
            <span>{uiText("Training Attendance")}</span>
            <span>{attendanceCount}/{requiredAttendance}</span>
          </div>
          <Progress
            value={Math.min((attendanceCount / requiredAttendance) * 100, 100)}
            className={`h-2 ${isEligibleForPromotion ? 'bg-green-100' : ''}`}
          />
          {isEligibleForPromotion && (
            <p className="text-sm text-green-600 mt-1 font-medium">
              {uiText("✓ Training requirement met for promotion!")}</p>
          )}
        </div>

        <div className="mb-4">
          <div className="flex justify-between text-sm mb-2">
            <span>{uiText("Overall Progress")}</span>
            <span>{Math.round(progressPercentage)}%</span>
          </div>
          <Progress value={progressPercentage} className="h-2" />
        </div>

        {nextBelt && (
          <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg">
            <p className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-1">
              {uiText("Next Belt Level:")}</p>
            <Badge variant="outline" className="border-blue-300 text-blue-700">
              {nextBelt.name}
            </Badge>
          </div>
        )}

        {!nextBelt && (
          <div className="p-4 bg-orange-50 dark:bg-orange-950 rounded-lg">
            <p className="text-sm font-medium text-orange-900 dark:text-orange-300 mb-1">
              {uiText("Highest Level Achieved")}</p>
            <p className="text-sm text-orange-700 dark:text-orange-400">
              {uiText("You've reached the highest level! Continue training for advanced techniques")}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CurrentBeltStatus;
